import React, { useCallback } from "react";
import { Node, Edge } from "reactflow";
import ELK from "elkjs/lib/elk.bundled.js";

// Layout constants
const NODE_WIDTH = 280;
const NODE_HEIGHT = 120;

// Initialize ELK layout engine
const elk = new ELK();

// ELK configuration optimized for decision trees
const elkOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.spacing.nodeNode': '80',
  'elk.layered.spacing.nodeNodeBetweenLayers': '120',
  'elk.layered.spacing.edgeNodeBetweenLayers': '30',
  'elk.spacing.edgeEdge': '10',
  'elk.spacing.edgeNode': '20',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.layered.cycleBreaking.strategy': 'GREEDY',
  'elk.partitioning.activate': 'true',
  'elk.separateConnectedComponents': 'false',
  'elk.insideSelfLoops.activate': 'true'
};

const detectCircularReferences = (nodes: Node[], edges: Edge[]): boolean => {
  // Create adjacency list including both regular edges and decision node paths
  const adjacencyList = new Map<string, Set<string>>();
  
  // Initialize adjacency list
  nodes.forEach(node => {
    adjacencyList.set(node.id, new Set());
  });
  
  // Add regular edges
  edges.forEach(edge => {
    const sourceSet = adjacencyList.get(edge.source);
    if (sourceSet) {
      sourceSet.add(edge.target);
    }
  });
  
  // Add decision node paths
  nodes.forEach(node => {
    if (node.data.entityType === "DECISION" && node.data.decisionData) {
      const sourceSet = adjacencyList.get(node.id);
      if (sourceSet) {
        if (node.data.decisionData.truePath) {
          sourceSet.add(node.data.decisionData.truePath);
        }
        if (node.data.decisionData.falsePath) {
          sourceSet.add(node.data.decisionData.falsePath);
        }
      }
    }
  });
  
  // Use DFS to detect cycles
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  
  const hasCycle = (nodeId: string): boolean => {
    if (recursionStack.has(nodeId)) {
      return true; // Found a back edge, indicating a cycle
    }
    
    if (visited.has(nodeId)) {
      return false; // Already processed this node
    }
    
    visited.add(nodeId);
    recursionStack.add(nodeId);
    
    const neighbors = adjacencyList.get(nodeId) || new Set();
    for (const neighbor of Array.from(neighbors)) {
      if (hasCycle(neighbor)) {
        return true;
      }
    }
    
    recursionStack.delete(nodeId);
    return false;
  };
  
  // Check for cycles starting from any unvisited node
  for (const node of nodes) {
    if (!visited.has(node.id) && hasCycle(node.id)) {
      return true;
    }
  }
  
  return false;
};

// Apply smart positioning using the reusable logic from nodeOperations
const applySmartPositioning = (
  nodes: Node[], 
  edges: Edge[], 
  manuallyMovedNodes: Set<string>, 
  calculateSmartPosition: (parentNode: Node, childNode: Node, allNodes: Node[], allEdges: Edge[]) => { x: number; y: number }
) => {
  console.log(`[DEBUG] Applying smart positioning using reusable logic`);
  
  return nodes.map(node => {
    // Skip manually positioned nodes
    if (manuallyMovedNodes.has(node.id)) {
      return node;
    }
    
    // Check if this node has a parent
    const parentEdge = edges.find(edge => edge.target === node.id);
    if (!parentEdge) return node;
    
    const parentNode = nodes.find(n => n.id === parentEdge.source);
    if (!parentNode) return node;
    
    // Skip if parent is manually positioned (we don't want to move its children)
    if (manuallyMovedNodes.has(parentNode.id)) {
      return node;
    }
    
    // Use the smart positioning logic from nodeOperations
    try {
      const newPosition = calculateSmartPosition(parentNode, node, nodes, edges);
      console.log(`[DEBUG] Smart positioning: moving ${node.id} to ${JSON.stringify(newPosition)}`);
      
      return {
        ...node,
        position: newPosition
      };
    } catch (error) {
      console.error(`[DEBUG] Failed to calculate smart position for node ${node.id}:`, error);
      return node;
    }
  });
};

// ELK-based layout function optimized for decision trees
const getLayoutedElements = async (
  nodes: Node[], 
  edges: Edge[], 
  manuallyMovedNodes: Set<string>,
  calculateSmartPosition: (parentNode: Node, childNode: Node, allNodes: Node[], allEdges: Edge[]) => { x: number; y: number }
) => {
  console.log(`[DEBUG] ELK Layout starting with ${nodes.length} nodes, preserving ${manuallyMovedNodes.size} manually positioned nodes`);
  
  // Filter out manually moved nodes for layout calculation
  const nodesToLayout = nodes.filter(node => !manuallyMovedNodes.has(node.id));
  const edgesToLayout = edges.filter(edge => 
    !manuallyMovedNodes.has(edge.source) && !manuallyMovedNodes.has(edge.target)
  );

  
  // If no nodes need layout, return original nodes unchanged
  if (nodesToLayout.length === 0) {
    console.log(`[DEBUG] No nodes need layout - returning original positions`);
    return nodes;
  }
  
  // Collect decision node paths for layout
  const decisionEdges: any[] = [];
  nodes.forEach(node => {
    if (node.data.entityType === "DECISION" && 
        node.data.decisionData && 
        !manuallyMovedNodes.has(node.id)) {
      const { truePath, falsePath } = node.data.decisionData;
      
      // Resolve identifiers to actual node IDs
      if (truePath) {
        const truePathNode = nodes.find(n => n.data.identifier === truePath || n.id === truePath);
        if (truePathNode && !manuallyMovedNodes.has(truePathNode.id)) {
          decisionEdges.push({
            id: `decision-true-${node.id}-${truePathNode.id}`,
            sources: [node.id],
            targets: [truePathNode.id]
          });
        }
      }
      
      if (falsePath) {
        const falsePathNode = nodes.find(n => n.data.identifier === falsePath || n.id === falsePath);
        if (falsePathNode && !manuallyMovedNodes.has(falsePathNode.id)) {
          decisionEdges.push({
            id: `decision-false-${node.id}-${falsePathNode.id}`,
            sources: [node.id],
            targets: [falsePathNode.id]
          });
        }
      }
    }
  });

  // Prepare the graph for ELK
  const graph = {
    id: "root",
    layoutOptions: elkOptions,
    children: nodesToLayout.map((node) => ({
      id: node.id,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    })),
    edges: [
      ...edgesToLayout.map((edge) => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
      })),
      ...decisionEdges
    ],
  };

  try {
    const layoutedGraph = await elk.layout(graph);
    
    // Map the layouted positions back to React Flow nodes
    let layoutedNodes = nodes.map((node) => {
      if (manuallyMovedNodes.has(node.id)) {
        // Keep manually positioned nodes where they are
        return node;
      }
      
      const layoutedNode = layoutedGraph.children?.find((n) => n.id === node.id);
      if (layoutedNode) {
        return {
          ...node,
          position: {
            x: layoutedNode.x || 0,
            y: layoutedNode.y || 0,
          },
        };
      }
      
      return node;
    });

    // Apply smart positioning using the reusable logic from nodeOperations
    layoutedNodes = applySmartPositioning(layoutedNodes, edges, manuallyMovedNodes, calculateSmartPosition);

    return layoutedNodes;
  } catch (error) {
    console.error("ELK layout failed:", error);
    // Return original nodes if layout fails
    return nodes;
  }
};

// Helper function to center children under their parents
const centerChildrenUnderParents = (nodes: Node[], edges: Edge[], manuallyMovedNodes: Set<string>) => {
  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  const processedNodes = new Set<string>();
  
  // Build parent-child relationships
  const childrenMap = new Map<string, string[]>();
  const parentMap = new Map<string, string>();
  
  edges.forEach(edge => {
    if (!childrenMap.has(edge.source)) {
      childrenMap.set(edge.source, []);
    }
    childrenMap.get(edge.source)!.push(edge.target);
    parentMap.set(edge.target, edge.source);
  });
  
  // Also handle decision node paths
  nodes.forEach(node => {
    if (node.data.entityType === "DECISION" && node.data.decisionData) {
      const { truePath, falsePath } = node.data.decisionData;
      
      if (!childrenMap.has(node.id)) {
        childrenMap.set(node.id, []);
      }
      
      if (truePath) {
        childrenMap.get(node.id)!.push(truePath);
        parentMap.set(truePath, node.id);
      }
      
      if (falsePath) {
        childrenMap.get(node.id)!.push(falsePath);
        parentMap.set(falsePath, node.id);
      }
    }
  });
  
  // Function to center children under a parent
  const centerChildren = (parentId: string) => {
    if (processedNodes.has(parentId)) return;
    processedNodes.add(parentId);
    
    const parent = nodeMap.get(parentId);
    const childIds = childrenMap.get(parentId) || [];
    
    if (!parent || childIds.length === 0 || manuallyMovedNodes.has(parentId)) return;
    
    // Filter out manually moved children
    const movableChildren = childIds.filter(childId => !manuallyMovedNodes.has(childId));
    if (movableChildren.length === 0) return;
    
    // Get child nodes
    const children = movableChildren.map(id => nodeMap.get(id)).filter(Boolean) as Node[];
    
    if (children.length === 1) {
      // Single child: center it under the parent
      const child = children[0];
      child.position.x = parent.position.x;
    } else if (children.length > 1) {
      // Multiple children: distribute them evenly around the parent
      children.sort((a, b) => a.position.x - b.position.x); // Sort by current x position
      
      const totalWidth = (children.length - 1) * (NODE_WIDTH + 60); // 60px spacing between nodes
      const startX = parent.position.x - totalWidth / 2;
      
      children.forEach((child, index) => {
        child.position.x = startX + index * (NODE_WIDTH + 60);
      });
    }
    
    // Recursively center grandchildren
    childIds.forEach(childId => centerChildren(childId));
  };
  
  // Find root nodes (nodes with no parents) and start centering from them
  const rootNodes = nodes.filter(node => !parentMap.has(node.id));
  rootNodes.forEach(rootNode => centerChildren(rootNode.id));
  
  return nodes;
};

// Hook for tree layout and positioning operations
export const useLayoutOperations = (
  nodes: Node[],
  edges: Edge[],
  setNodes: (nodes: Node[]) => void,
  nodeDimensions: Record<string, number>,
  manuallyMovedNodes: Set<string>,
  setManuallyMovedNodes: React.Dispatch<React.SetStateAction<Set<string>>>,
  setNodeDimensions: (dimensions: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)) => void,
  resizeTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
  calculateSmartPosition: (parentNode: Node, childNode: Node, allNodes: Node[], allEdges: Edge[]) => { x: number; y: number }
) => {
  // ELK-based layout with performance optimization and manual positioning preservation
  const applyTopDownTreeLayout = useCallback(async (forceReposition: boolean = false) => {
    console.log(`[DEBUG] Layout requested with forceReposition: ${forceReposition}, nodes: ${nodes.length}`);
    
    if (nodes.length <= 1) return;
    
    // Skip layout for very large trees to prevent performance issues
    if (nodes.length > 150 && !forceReposition) {
      console.warn("[ConversationTree] Skipping automatic layout for large tree (>150 nodes). Use manual repositioning if needed.");
      return;
    }
    
    // For small trees with lots of manually positioned nodes, skip ELK layout
    const manuallyPositionedCount = manuallyMovedNodes.size;
    const totalNodes = nodes.length;
    const manualPercentage = totalNodes > 0 ? manuallyPositionedCount / totalNodes : 0;
    
    if (manualPercentage > 0.5 && !forceReposition) {
      console.log("[ConversationTree] Skipping ELK layout - too many manually positioned nodes");
      return;
    }
    
    try {
      console.log(`[DEBUG] Starting ELK layout with ${nodes.length} nodes, ${edges.length} edges`);
      console.log(`[DEBUG] Manually moved nodes to preserve:`, Array.from(manuallyMovedNodes));
      
      // Use ELK to layout nodes, preserving manually moved ones
      const layoutedNodes = await getLayoutedElements(nodes, edges, manuallyMovedNodes, calculateSmartPosition);
      
      console.log(`[DEBUG] ELK layout completed, applying new positions`);
      // Apply the new positions
      setNodes(layoutedNodes);
    } catch (error) {
      console.error("[ConversationTree] Layout failed:", error);
      // Fallback: keep current positions
    }
  }, [nodes, edges, manuallyMovedNodes, setNodes]);

  // Force repositioning of all nodes with smart preservation
  const forceRepositionAllNodes = useCallback(async () => {
    console.log(`[DEBUG] forceRepositionAllNodes called - starting reorganization`);
    console.log(`[DEBUG] Current nodes: ${nodes.length}, manually moved: ${manuallyMovedNodes.size}`);
    
    // FIXED: Be much more conservative about clearing manually positioned nodes
    setManuallyMovedNodes(prev => {
      const nodesToKeep = new Set<string>();
      
      // Keep nodes that have children OR are decision node children
      prev.forEach(nodeId => {
        const node = nodes.find(n => n.id === nodeId);
        const hasChildren = edges.some(edge => edge.source === nodeId);
        const isDecisionNode = node?.data.entityType === "DECISION" && 
          node?.data.decisionData && (node.data.decisionData.truePath || node.data.decisionData.falsePath);
        
        // Keep if it's a decision node, has children, or is a child of a decision node
        const isDecisionChild = edges.some(edge => {
          const parent = nodes.find(n => n.id === edge.source);
          return edge.target === nodeId && parent?.data.entityType === "DECISION";
        });
        
        if (hasChildren || isDecisionNode || isDecisionChild) {
          nodesToKeep.add(nodeId);
        }
      });
      
      return nodesToKeep;
    });
    
    // Call top-down tree layout with force flag
    await applyTopDownTreeLayout(true);
  }, [applyTopDownTreeLayout, setManuallyMovedNodes, edges, nodes]);

  // Handle node resize without automatic repositioning
  const handleNodeResize = useCallback((nodeId: string, height: number) => {
    setNodeDimensions(prev => {
      // Only update if height is different (prevents endless loop)
      if (prev[nodeId] !== height) {
        // Cancel previous timeout if it exists
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
          resizeTimeoutRef.current = null;
        }
        
        // Just update dimensions but don't reposition
        return { ...prev, [nodeId]: height };
      }
      return prev;
    });
  }, [setNodeDimensions, resizeTimeoutRef]);

  return {
    applyTopDownTreeLayout,
    forceRepositionAllNodes,
    handleNodeResize
  };
};